import { Buffer } from "buffer";
import gost89 from "gost89";
import Box, { std_curve } from "jkurwa";

import {
  createErrorResult,
  createSuccessResult,
  ERRORS,
  IKeyBox,
  SimpleResult,
} from "@prostocheck/shared";

import { createGatewayQuery } from "../gateway";
import {
  KUPYNA_KEYID_ALGO,
  loadModernStore,
  looksLikeModernStore,
} from "../modern-store";
import { ILoadKeyParams, ILoadModernKeyParams } from "./key-box.models";
import { diagnoseKeyError, stripContainerHeader } from "./key-box.utils";

/**
 * Two undeclared identifiers inside jkurwa 1.22.7 (`jk` in models/Priv.js,
 * `i` in util.js) resolve to the global scope. Providing them is what keeps
 * .zs2 and .jks containers loadable — see ai/docs/kep-signing.md.
 */
const installJkurwaWorkarounds = (): void => {
  const scope = globalThis as typeof globalThis & {
    jk?: { std_curve: unknown };
    i?: number;
  };

  scope.jk = scope.jk ?? { std_curve };
  scope.i = scope.i ?? 0;
};

/**
 * `cmpGatewayUrl` is the ЦСК transport (`getCmpGatewayUrl`): `box.query` uses
 * it for TSP time stamps, and `fetchCertificates` reads it back off the box for
 * the CMP lookup. The fiscal server has its own gateway, passed to
 * `createFiscalClient` separately.
 */
export const createKeyBox = (cmpGatewayUrl: string): IKeyBox => {
  installJkurwaWorkarounds();

  return {
    box: new Box({
      algo: gost89.compat.algos(),
      query: createGatewayQuery(cmpGatewayUrl),
    }),
    cmpGatewayUrl,
  };
};

/**
 * Decrypts a key container in memory and reports how many private keys it held.
 * The bytes and the password never leave this call.
 */
export const loadKey = ({
  keyBox,
  file,
  password,
}: ILoadKeyParams): SimpleResult<number> => {
  const { box } = keyBox;
  const content = stripContainerHeader(Buffer.from(file));

  try {
    box.load({ keyBuffers: [content], password });
  } catch (loadError) {
    const modern = loadModernKey({ keyBox, content, password });

    if (modern) return modern;

    return createErrorResult(
      diagnoseKeyError({ content, password, algo: box.algo }),
      loadError instanceof Error ? loadError.message : undefined,
      loadError,
    );
  }

  const keyCount = box.keys.filter(key => key.priv).length;

  if (keyCount === 0) return createErrorResult(ERRORS.key.noPrivateKey);

  return createSuccessResult(keyCount);
};

/**
 * A container issued on the post-2022 standards (Купина/Калина) throws from
 * `box.load` — jkurwa 1.22.7 has no algorithm for it. Read it with
 * `libs/kep/src/modern-store` instead and, on success, mark the box so the CMP
 * lookup hashes the key id with Купина rather than ГОСТ 34.311. `null` means
 * "not a modern container", and `loadKey` then runs the normal diagnosis.
 * See ai/docs/kep-signing.md.
 */
const loadModernKey = ({
  keyBox,
  content,
  password,
}: ILoadModernKeyParams): SimpleResult<number> | null => {
  const { box } = keyBox;

  let store;

  try {
    store = loadModernStore({ file: content, password });
  } catch (modernError) {
    // A modern bag that will not decrypt is, in practice, a wrong password —
    // the plaintext is garbage and `Priv.from_asn1` rejects it.
    if (looksLikeModernStore(content)) {
      return createErrorResult(
        ERRORS.key.wrongPassword,
        modernError instanceof Error ? modernError.message : undefined,
        modernError,
      );
    }

    return null;
  }

  if (!store) return null;

  keyBox.keyidAlgo = KUPYNA_KEYID_ALGO;
  store.keys.forEach(priv => box.add({ priv }));
  box._indexKeys();

  const keyCount = box.keys.filter(key => key.priv).length;

  if (keyCount === 0) return createErrorResult(ERRORS.key.noPrivateKey);

  return createSuccessResult(keyCount);
};

/** True once at least one private key is paired with its certificate. */
export const isSigningReady = ({ box }: IKeyBox): boolean =>
  box.keys.some(key => Boolean(key.priv) && Boolean(key.cert));
